/* mien-cutout.js — turns uniform light-background product JPGs in the fit builder
   into transparent cutouts at render time. Samples the image border to find the
   actual background color (pure white OR light studio grey), then flood-fills
   from the edges inward, and finally clears enclosed background pockets (between
   jeans legs, arm gaps) with a size-guarded component pass. Busy borders
   (on-body / lifestyle shots) and dark backdrops are left alone.
   Fails silently: on any error the original image stays. */
(function () {
  var DELTA = 16;        /* hard match distance from the sampled bg color */
  var SOFT_DELTA = 30;   /* soft fringe distance — gets partial alpha */
  var MIN_BG_LUMA = 198; /* only key light backgrounds; darker = real photo */
  var AGREE = 0.7;       /* share of border samples that must match the bg estimate */
  var MAX_HOLE = 0.30;   /* enclosed pockets larger than this share of the image are kept */
  var PROCESSED = 'data-cutout-done';

  function cutout(img) {
    if (!img || img.getAttribute(PROCESSED)) return;
    var src = img.currentSrc || img.src;
    if (!src) return;
    var isPng = /\.png(\?|$)/i.test(src);
    if (!isPng && !/\.jpe?g(\?|$)/i.test(src)) return;
    img.setAttribute(PROCESSED, '1');
    /* PNGs never multiply: real cutouts would lose white fabric to the blend, and
       baked-background ones get real transparency from the flood fill below */
    if (isPng) img.style.mixBlendMode = 'normal';

    var probe = new Image();
    probe.crossOrigin = 'anonymous';
    probe.onload = function () {
      try {
        var w = probe.naturalWidth, h = probe.naturalHeight;
        if (!w || !h) return;
        var cv = document.createElement('canvas');
        cv.width = w; cv.height = h;
        var cx = cv.getContext('2d', { willReadFrequently: true });
        cx.drawImage(probe, 0, 0);
        var im = cx.getImageData(0, 0, w, h);
        var d = im.data;

        /* a PNG can be a real transparent cutout OR a photo with a baked studio
           background — sample the frame border's alpha to tell them apart */
        var realCutout = false;
        var stepX = Math.max(1, w >> 5), stepY = Math.max(1, h >> 5);
        if (isPng) {
          var clear = 0, asamples = 0;
          for (var ax = 0; ax < w; ax += stepX) {
            if (d[ax * 4 + 3] < 16) clear++;
            if (d[(((h - 1) * w) + ax) * 4 + 3] < 16) clear++;
            asamples += 2;
          }
          for (var ay = 0; ay < h; ay += stepY) {
            if (d[(ay * w) * 4 + 3] < 16) clear++;
            if (d[((ay * w) + w - 1) * 4 + 3] < 16) clear++;
            asamples += 2;
          }
          realCutout = clear > asamples * 0.5;
        }

        if (!realCutout) {
          /* sample the border to estimate the background color */
          var samples = [];
          function push(p) {
            var i4 = p * 4;
            if (d[i4 + 3] > 200) samples.push([d[i4], d[i4 + 1], d[i4 + 2]]);
          }
          for (var sx = 0; sx < w; sx += stepX) { push(sx); push((h - 1) * w + sx); }
          for (var sy = 0; sy < h; sy += stepY) { push(sy * w); push(sy * w + w - 1); }
          if (!samples.length) return;
          function med(k) {
            var a = samples.map(function (s) { return s[k]; }).sort(function (x, y) { return x - y; });
            return a[a.length >> 1];
          }
          var br = med(0), bgc = med(1), bb = med(2);
          var luma = 0.2126 * br + 0.7152 * bgc + 0.0722 * bb;
          var agree = 0;
          for (var si = 0; si < samples.length; si++) {
            var s = samples[si];
            if (Math.abs(s[0] - br) <= SOFT_DELTA && Math.abs(s[1] - bgc) <= SOFT_DELTA && Math.abs(s[2] - bb) <= SOFT_DELTA) agree++;
          }
          /* dark or busy border = a real photo, not a studio shot — leave it alone */
          if (luma < MIN_BG_LUMA || agree < samples.length * AGREE) return;

          function dist(i4) {
            var dr = Math.abs(d[i4] - br), dg = Math.abs(d[i4 + 1] - bgc), db = Math.abs(d[i4 + 2] - bb);
            return dr > dg ? (dr > db ? dr : db) : (dg > db ? dg : db);
          }

          var visited = new Uint8Array(w * h);
          var stack = [];
          for (var x = 0; x < w; x++) {
            if (dist(x * 4) <= DELTA) stack.push(x);
            var b = (h - 1) * w + x;
            if (dist(b * 4) <= DELTA) stack.push(b);
          }
          for (var y = 0; y < h; y++) {
            var l = y * w;
            if (dist(l * 4) <= DELTA) stack.push(l);
            var r = y * w + (w - 1);
            if (dist(r * 4) <= DELTA) stack.push(r);
          }

          while (stack.length) {
            var p = stack.pop();
            if (visited[p]) continue;
            visited[p] = 1;
            var i4 = p * 4;
            var dd = dist(i4);
            if (dd > DELTA) {
              if (dd <= SOFT_DELTA) d[i4 + 3] = Math.round(d[i4 + 3] * 0.35);
              continue;
            }
            d[i4 + 3] = 0;
            var px = p % w, py = (p / w) | 0;
            if (px > 0) stack.push(p - 1);
            if (px < w - 1) stack.push(p + 1);
            if (py > 0) stack.push(p - w);
            if (py < h - 1) stack.push(p + w);
          }

          /* second pass: clear enclosed background pockets — bg-colored regions the
             edge fill couldn't reach (between legs, arm gaps). size-guarded so a
             white garment body (a huge bg-colored region) is never hollowed out. */
          var maxHole = w * h * MAX_HOLE;
          var seen = new Uint8Array(w * h);
          for (var p0 = 0; p0 < w * h; p0++) {
            if (visited[p0] || seen[p0]) continue;
            if (dist(p0 * 4) > DELTA) continue;
            var q = [p0], head = 0;
            seen[p0] = 1;
            while (head < q.length) {
              var pc = q[head++];
              var cx2 = pc % w, cy2 = (pc / w) | 0;
              if (cx2 > 0 && !seen[pc - 1] && !visited[pc - 1] && dist((pc - 1) * 4) <= DELTA) { seen[pc - 1] = 1; q.push(pc - 1); }
              if (cx2 < w - 1 && !seen[pc + 1] && !visited[pc + 1] && dist((pc + 1) * 4) <= DELTA) { seen[pc + 1] = 1; q.push(pc + 1); }
              if (cy2 > 0 && !seen[pc - w] && !visited[pc - w] && dist((pc - w) * 4) <= DELTA) { seen[pc - w] = 1; q.push(pc - w); }
              if (cy2 < h - 1 && !seen[pc + w] && !visited[pc + w] && dist((pc + w) * 4) <= DELTA) { seen[pc + w] = 1; q.push(pc + w); }
            }
            if (q.length <= maxHole) {
              for (var mi = 0; mi < q.length; mi++) d[q[mi] * 4 + 3] = 0;
            }
          }

          cx.putImageData(im, 0, 0);
        }

        /* crop to the garment's alpha bounding box (+3% air): loose frames made
           some pieces render tiny while tight cutouts filled the whole stage —
           a uniform crop is what keeps every slot's garment the same visual size */
        var minX = w, minY = h, maxX = -1, maxY = -1;
        for (var idx = 0, a3 = 3; idx < w * h; idx++, a3 += 4) {
          if (d[a3] > 8) {
            var gx = idx % w, gy = (idx / w) | 0;
            if (gx < minX) minX = gx;
            if (gx > maxX) maxX = gx;
            if (gy < minY) minY = gy;
            if (gy > maxY) maxY = gy;
          }
        }
        if (maxX <= minX || maxY <= minY) { img.style.mixBlendMode = 'normal'; return; }
        var mx = Math.round((maxX - minX) * 0.03) + 4, my = Math.round((maxY - minY) * 0.03) + 4;
        var cl = Math.max(0, minX - mx), ct = Math.max(0, minY - my);
        var cw = Math.min(w, maxX + 1 + mx) - cl, ch = Math.min(h, maxY + 1 + my) - ct;
        /* already tight (e.g. our pre-trimmed metafield cutouts): skip the re-encode */
        if (realCutout && cw * ch > 0.88 * w * h) { img.style.mixBlendMode = 'normal'; return; }
        var out = document.createElement('canvas');
        out.width = cw; out.height = ch;
        out.getContext('2d').drawImage(cv, cl, ct, cw, ch, 0, 0, cw, ch);
        out.toBlob(function (blob) {
          if (!blob) return;
          var url = URL.createObjectURL(blob);
          img.src = url;
          /* the cutout PNG has real transparency — multiply would make its light
             pixels vanish into the wave background */
          img.style.mixBlendMode = 'normal';
          img.srcset = '';
        }, 'image/png');
      } catch (e) { /* leave the original */ }
    };
    probe.onerror = function () { /* leave the original */ };
    probe.src = src;
  }

  function run(root) {
    (root || document).querySelectorAll('.fitb-item img').forEach(function (img) {
      if (img.complete) cutout(img);
      else img.addEventListener('load', function once() {
        img.removeEventListener('load', once);
        cutout(img);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { run(); });
  } else {
    run();
  }
})();
